import type { InputHTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { MIN_PASSWORD_LENGTH } from '@shared/constants'
import { IconButton, Input } from '@/components/ui'
import { cn } from '@/lib/cn'
import { pluralize } from '@/lib/format'

export interface PasswordStrength {
  /** 0 empty, 1 short of the minimum, 2–4 increasing quality. */
  score: 0 | 1 | 2 | 3 | 4
  label: string
  meetsMinimum: boolean
}

const CHARACTER_CLASSES = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/]

/**
 * Length first, variety second — the server only enforces `MIN_PASSWORD_LENGTH`,
 * so the meter's job is to make that requirement legible before submitting.
 */
export function passwordStrength(value: string): PasswordStrength {
  if (!value) {
    return {
      score: 0,
      label: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
      meetsMinimum: false
    }
  }

  const missing = MIN_PASSWORD_LENGTH - value.length
  if (missing > 0) {
    return {
      score: 1,
      label: `${missing} more ${pluralize(missing, 'character')} needed.`,
      meetsMinimum: false
    }
  }

  const classes = CHARACTER_CLASSES.filter((pattern) => pattern.test(value)).length
  const long = value.length >= 16

  if (classes >= 3 && long) return { score: 4, label: 'Strong password.', meetsMinimum: true }
  if (classes >= 3 || long) return { score: 3, label: 'Good password.', meetsMinimum: true }

  return { score: 2, label: 'Fair — add capitals, numbers or symbols.', meetsMinimum: true }
}

const SEGMENT_TONES: Record<1 | 2 | 3 | 4, string> = {
  1: 'bg-bad-500',
  2: 'bg-warn-500',
  3: 'bg-info-500',
  4: 'bg-ok-500'
}

const LABEL_TONES: Record<1 | 2 | 3 | 4, string> = {
  1: 'text-bad-600 dark:text-bad-300',
  2: 'text-warn-700 dark:text-warn-300',
  3: 'text-info-700 dark:text-info-300',
  4: 'text-ok-700 dark:text-ok-300'
}

const SEGMENTS = [1, 2, 3, 4] as const

function StrengthMeter({
  id,
  strength
}: {
  id: string
  strength: PasswordStrength
}): ReactElement {
  const { score, label } = strength

  return (
    <div className="mt-2">
      <div className="flex gap-1" aria-hidden="true">
        {SEGMENTS.map((step) => (
          <span
            key={step}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-colors duration-200',
              score !== 0 && step <= score ? SEGMENT_TONES[score] : 'bg-line'
            )}
          />
        ))}
      </div>
      {/* Announced politely: it changes on every keystroke, so it must not interrupt. */}
      <p
        id={id}
        aria-live="polite"
        className={cn(
          'mt-1.5 text-xs font-medium',
          score === 0 ? 'text-fg-muted' : LABEL_TONES[score]
        )}
      >
        {label}
      </p>
    </div>
  )
}

export interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  hint?: string
  error?: string
  leadingIcon?: ReactNode
  /** Shows the live strength meter. Use it for new passwords, never for sign-in. */
  showStrength?: boolean
  ref?: Ref<HTMLInputElement>
}

export default function PasswordField({
  label,
  hint,
  error,
  leadingIcon,
  showStrength = false,
  id,
  value,
  disabled,
  className,
  ref,
  'aria-describedby': describedBy,
  ...rest
}: PasswordFieldProps): ReactElement {
  const generatedId = useId()
  const fieldId = id ?? `password-${generatedId}`
  const strengthId = `${fieldId}-strength`
  const [revealed, setRevealed] = useState(false)

  // The meter only reads controlled string values; anything else scores as empty.
  const text = typeof value === 'string' ? value : ''
  const strength = passwordStrength(text)
  const described =
    [describedBy, showStrength ? strengthId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className="w-full">
      {/* Rendered here rather than via Input's own `label`, so the reveal toggle can
          be positioned against the field itself and not the whole control. */}
      {label ? (
        <label htmlFor={fieldId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <Input
          ref={ref}
          id={fieldId}
          type={revealed ? 'text' : 'password'}
          value={value}
          hint={hint}
          error={error}
          leadingIcon={leadingIcon}
          disabled={disabled}
          aria-describedby={described}
          className={cn('pr-11', className)}
          {...rest}
        />
        <span className="absolute right-1 top-1">
          <IconButton
            type="button"
            size="sm"
            disabled={disabled}
            aria-pressed={revealed}
            label={revealed ? 'Hide password' : 'Show password'}
            onClick={() => setRevealed((current) => !current)}
          >
            {revealed ? <EyeOff /> : <Eye />}
          </IconButton>
        </span>
      </div>

      {showStrength ? <StrengthMeter id={strengthId} strength={strength} /> : null}
    </div>
  )
}

export { PasswordField }
